/**
 * Staff portal — My Files (standalone route)
 *
 * Mostly delegates to [MyFilesPanel], which is also embedded as a tab inside
 * /app/members/my-profile. The route is kept around so existing links + the
 * Profile tab's "open in full page" gesture both work, but the menu entry
 * has been removed in favour of the Profile tab.
 */

import { useQuery } from '@tanstack/react-query';
import {
  Stack,
  Title,
  Text,
  Group,
  Tooltip,
} from '@/components/ui';
import ActionIcon from '@/components/ui/ActionIcon';
import { FadeIn } from '@/components/animations';
import { PageHelpButton } from '@/components/help';
import { IconRefresh } from '@tabler/icons-react';
import { useStaffPortalFileService } from '@actschurch/ccms-sdk';
import { hrKeys } from '../utils/queryKeys';
import { MyFilesPanel } from '../my-profile/components/MyFilesPanel';

export default function MyFilesPage() {
  const fileService = useStaffPortalFileService();
  const { isFetching, refetch } = useQuery({
    queryKey: hrKeys.myFiles(),
    queryFn: () => fileService.getMyFiles(),
  });

  return (
    <FadeIn>
      <Stack gap="lg">
        <Group justify="space-between" wrap="wrap">
          <Stack gap={0}>
            <Title order={2}>My Files</Title>
            <Text size="sm" c="dimmed">
              Your personal HR files. HR-only files (e.g. payroll documents) are not visible here.
            </Text>
          </Stack>
          <Group gap="sm" data-help-id="hr.my-files.actions">
            <Tooltip label="Refresh">
              <ActionIcon variant="subtle" onClick={() => refetch()} loading={isFetching}>
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
            <PageHelpButton pageKey="hr.my-files" />
          </Group>
        </Group>

        <MyFilesPanel helpIdPrefix="hr.my-files" />
      </Stack>
    </FadeIn>
  );
}
